import React, { ChangeEvent, useEffect, useState } from "react";
import { isFQDN } from "validator";

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  useAddCustomDomainMutation,
  useGetClusterQuery,
} from "../../../store/pomeriumApi";

type AddCustomDomainDialogProps = {
  organizationId: string;
  clusterId: string;
  open: boolean;
  onClose: () => void;
};

const AddCustomDomainDialog = ({
  organizationId,
  clusterId,
  open,
  onClose,
}: AddCustomDomainDialogProps) => {
  const { data: cluster } = useGetClusterQuery(
    { organizationId, clusterId },
    { skip: !organizationId || !clusterId },
  );
  const [addCustomDomain, { isLoading: isAdding }] =
    useAddCustomDomainMutation();
  const [rawCustomDomain, setRawCustomDomain] = useState("");
  const [customDomainError, setCustomDomainError] = useState("");
  const customDomain = rawCustomDomain.startsWith("*.")
    ? rawCustomDomain.substring(2)
    : rawCustomDomain;

  useEffect(() => {
    if (!isFQDN(customDomain)) {
      setCustomDomainError("Invalid domain name. Must be a FQDN.");
    } else {
      setCustomDomainError("");
    }
  }, [customDomain]);

  useEffect(() => {
    if (!open) {
      setRawCustomDomain("");
    }
  }, [open]);

  const handleAdd = async () => {
    try {
      await addCustomDomain({
        organizationId,
        customDomainProperties: {
          clusterId,
          domainName: customDomain,
        },
      });
    } finally {
      onClose();
    }
  };
  const handleChange = (evt: ChangeEvent<HTMLInputElement>) =>
    setRawCustomDomain(evt.target.value);
  const handleClose = () => onClose();

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Add Custom Domain</DialogTitle>
      <Divider />
      <DialogContent>
        <Stack direction="row" alignItems="center" spacing={3}>
          <Typography variant="h6">Domain Name</Typography>
          <Box flexGrow={1}>
            <TextField
              autoFocus
              margin="normal"
              id="custom-domain-name"
              name="custom-domain-name"
              placeholder="e.g. example.com"
              variant="outlined"
              size="small"
              fullWidth
              value={rawCustomDomain}
              onChange={handleChange}
              error={!!customDomainError}
              helperText={customDomainError}
            />
          </Box>
        </Stack>
        {cluster?.flavor === "hosted" ? (
          <DialogContentText component="div">
            <p>
              To use a custom domain with a hosted cluster, add the following
              DNS records:
            </p>
            <ol>
              <li>
                <strong>Traffic routing:</strong> Add a wildcard CNAME record
                for{" "}
                {customDomain && !customDomainError ? (
                  <strong>*.{customDomain}</strong>
                ) : (
                  <>your domain</>
                )}{" "}
                pointing to <strong>{cluster?.fqdn}</strong>
              </li>
              <li>
                <strong>Certificate provisioning:</strong> Add a CNAME record
                for{" "}
                {customDomain && !customDomainError ? (
                  <strong>_acme-challenge.{customDomain}</strong>
                ) : (
                  <>_acme-challenge.your-domain</>
                )}{" "}
                pointing to <strong>_acme-challenge.{cluster?.fqdn}</strong>
              </li>
            </ol>
          </DialogContentText>
        ) : (
          <DialogContentText>
            To use a custom domain, add a wildcard CNAME record for{" "}
            {customDomain && !customDomainError ? (
              <strong>*.{customDomain}</strong>
            ) : (
              <>your domain</>
            )}{" "}
            and point it to <strong>{cluster?.fqdn}</strong>.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAdd}
          disabled={!customDomain || !!customDomainError || isAdding}
        >
          {isAdding && <CircularProgress />}
          Add Custom Domain
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default AddCustomDomainDialog;
